import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import StateContext from '../context/State';
import PortalService from '../services/portal.service';

/**
 * Custom hook to fetch retainer configuration and calculate if it's required
 * @param enabled - Whether the query should run (typically based on modal visibility)
 * @returns Object with:
 *   - retainerConfig: The full retainer configuration
 *   - isRetainerRequired: Computed boolean indicating if any retainer is required
 *   - isLoading: Whether the query is currently fetching
 *   - error: Any error that occurred during fetch
 *   - refetch: Function to manually refetch the data
 */
export function useRetainerInformationQuery(enabled = true) {
    const { state } = useContext(StateContext);
    const portalService = new PortalService(state);
    
    const query = useQuery({
        queryKey: ['retainerInformation', state.portalId],
        queryFn: async () => {
            const retainerInfo = await portalService.getClientPortalRetainerInformation(state.portalId);
            
            const isRequired = 
                retainerInfo.DefaultRetainerRequired || 
                retainerInfo.TextBoxRetainerRequired || 
                retainerInfo.UploadRetainer;
            
            return {
                config: retainerInfo,
                isRequired: isRequired
            };
        },
        enabled: enabled && Boolean(state.portalId), // Only fetch when enabled and we have a valid portalId
        staleTime: 0,          // Always consider stale (refetch when modal opens)
        refetchOnMount: true,  // Refetch each time the component mounts
    });

    return {
        retainerConfig: query.data?.config,
        isRetainerRequired: query.data?.isRequired ?? false,
        isLoading: query.isLoading,
        error: query.error,
        refetch: query.refetch
    };
}

